import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

function formatError(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      <div className="pointer-events-none fixed -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#E2FF31]/10 blur-[120px]" />

      <header className="px-12 lg:px-20 pt-10">
        <Link
          to="/"
          data-testid="back-home"
          className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-2.5 text-sm font-medium tracking-wide transition-all outline-none focus-visible:bg-[#E2FF31] focus-visible:text-black focus-visible:scale-105 hover:bg-white/10"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to catalog
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-lg">
          <div className="mb-10 text-left">
            <div className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 mb-6">
              <Lock className="w-4 h-4 text-[#E2FF31]" />
              <span className="text-xs uppercase tracking-[0.3em] text-white/60">Admin access</span>
            </div>
            <h1 className="font-heading text-5xl lg:text-6xl font-black tracking-tighter leading-none mb-4">
              Sign in
            </h1>
            <p className="text-lg text-white/50 max-w-md">
              Enter your credentials to manage the catalog, upload logos, and publish apps.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-6" data-testid="login-form">
            <div className="flex flex-col gap-2">
              <Label htmlFor="email" className="text-sm uppercase tracking-[0.2em] text-white/50">Username</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                className="h-14 text-lg bg-[#121212] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#E2FF31] focus-visible:border-[#E2FF31]"
                placeholder="Enter your username"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="password" className="text-sm uppercase tracking-[0.2em] text-white/50">Password</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="h-14 text-lg bg-[#121212] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#E2FF31] focus-visible:border-[#E2FF31]"
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="h-14 rounded-full bg-[#E2FF31] text-black text-lg font-bold hover:bg-[#c9e628] focus-visible:ring-4 focus-visible:ring-[#E2FF31]/50 focus-visible:scale-[1.02] transition-all"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
